import { Loader2 } from "lucide-react";
import { useEffect, useState, type PointerEvent, type ReactNode } from "react";
import { useLocale, useT } from "../i18n";
import { apiJson } from "../lib/api";
import { fillDaily, groupDaily, pct, tickStep, type TrendPoint } from "../lib/stats";
import type { StatsRange, TourStats, TourStatsScene } from "../../shared/api-types";

// Statistik-Tab einer Tour. Bewusst simples Inline-SVG statt Chart-Library —
// ein Balken je Tag reicht, und die Seite bleibt ohne zusätzliche Abhängigkeit.
// Farben (validiert für Farbfehlsichtigkeit, hell und dunkel): Aufrufe blau, Besucher amber.
const VIEWS_FILL = "fill-blue-600 dark:fill-blue-500";
const VISITORS_FILL = "fill-amber-600";

function Tile({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900">
      <p className="text-2xl font-semibold tabular-nums">{value}</p>
      <p className="text-sm text-zinc-600 dark:text-zinc-300">{label}</p>
      {hint && <p className="mt-0.5 text-[11px] text-zinc-400 dark:text-zinc-500">{hint}</p>}
    </div>
  );
}

function Section({
  title,
  hint,
  children,
  className = "",
}: {
  title: string;
  hint?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900 ${className}`}
    >
      <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-400">{title}</h3>
      {hint && <p className="mt-1 text-xs text-zinc-400 dark:text-zinc-500">{hint}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}

function StatBar({
  label,
  value,
  total,
  suffix,
}: {
  label: string;
  value: number;
  total: number;
  suffix?: string;
}) {
  const share = pct(value, total);
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-36 shrink-0 truncate text-zinc-500 dark:text-zinc-400">{label}</span>
      <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
        <span className="block h-full rounded-full bg-accent" style={{ width: `${share}%` }} />
      </span>
      <span className="w-20 shrink-0 text-right tabular-nums">
        {value}
        {suffix} <span className="text-zinc-400">({share} %)</span>
      </span>
    </div>
  );
}

function RangeToggle({
  range,
  onChange,
}: {
  range: StatsRange;
  onChange: (r: StatsRange) => void;
}) {
  const t = useT();
  const label = { 30: t.stats.range30, 90: t.stats.range90, all: t.stats.rangeAll };
  return (
    <div className="flex rounded-lg border border-zinc-200 p-0.5 text-xs dark:border-zinc-700">
      {([30, 90, "all"] as const).map((r) => (
        <button
          key={r}
          onClick={() => onChange(r)}
          className={`rounded-md px-2.5 py-1 font-medium transition-colors ${
            range === r
              ? "bg-accent text-white"
              : "text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200"
          }`}
        >
          {label[r]}
        </button>
      ))}
    </div>
  );
}

export function TourStatsPanel({ tourId: id }: { tourId: string }) {
  const t = useT();
  const [locale] = useLocale();
  const [range, setRange] = useState<StatsRange>(30);
  const [stats, setStats] = useState<TourStats | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setError(false);
    apiJson<TourStats>(`/api/tours/${id}/stats?days=${range}`)
      .then((s) => alive && setStats(s))
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
    };
  }, [id, range]);

  const fmtDay = (date: string) =>
    new Date(`${date}T00:00:00Z`).toLocaleDateString(locale === "de" ? "de-DE" : "en-GB", {
      day: "2-digit",
      month: "2-digit",
      timeZone: "UTC",
    });
  const fmtDate = (date: string) =>
    new Date(`${date}T00:00:00Z`).toLocaleDateString(locale === "de" ? "de-DE" : "en-GB", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "UTC",
    });
  const fmtDuration = (s: number) =>
    s < 60 ? t.stats.seconds(s) : t.stats.minutes(Math.floor(s / 60), s % 60);
  const langName = (code: string) => {
    try {
      return new Intl.DisplayNames([locale], { type: "language" }).of(code) ?? code;
    } catch {
      return code;
    }
  };

  const ready = stats && stats.range === range;
  const points = ready ? groupDaily(fillDaily(stats)) : [];
  const eng = stats?.engagement;
  const sessions = eng?.sessions ?? 0;
  const rate = (n: number) => (sessions > 0 ? `${pct(n, sessions)} %` : "–");
  const directViews = stats
    ? Math.max(0, stats.totals.views - stats.referrers.reduce((n, r) => n + r.views, 0))
    : 0;

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <RangeToggle range={range} onChange={setRange} />
      </div>

      {error ? (
        <p className="py-16 text-center text-sm text-red-600 dark:text-red-400">
          {t.stats.loadError}
        </p>
      ) : !ready || !eng ? (
        <div className="grid place-items-center py-24">
          <Loader2 className="size-5 animate-spin text-zinc-400" />
        </div>
      ) : stats.totals.views === 0 ? (
        <p className="py-16 text-center text-sm text-zinc-400 dark:text-zinc-500">
          {t.stats.empty}
        </p>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Tile label={t.stats.views} value={stats.totals.views} />
            <Tile
              label={t.stats.visitors}
              value={stats.totals.visitors}
              hint={t.stats.visitorsHint}
            />
            <Tile
              label={t.stats.avgDuration}
              value={sessions > 0 ? fmtDuration(eng.avgSeconds) : "–"}
              hint={t.stats.avgDurationHint}
            />
            <Tile label={t.stats.bounceRate} value={rate(eng.bounces)} hint={t.stats.bounceHint} />
            <Tile
              label={t.stats.interactionRate}
              value={rate(eng.interacted)}
              hint={t.stats.interactionHint}
            />
            <Tile label={t.stats.hotspotClicks} value={eng.hotspotClicks} />
          </div>

          <Section title={t.stats.trend}>
            <TrendChart points={points} fmtDay={fmtDay} fmtDate={fmtDate} />
          </Section>

          <div className="grid gap-4 sm:grid-cols-2">
            <Section title={t.stats.duration} hint={t.stats.durationHint}>
              <div className="space-y-1.5">
                {eng.buckets.map((n, i) => (
                  <StatBar key={i} label={t.stats.buckets[i]!} value={n} total={sessions} />
                ))}
              </div>
              <p className="mt-3 text-xs text-zinc-400 dark:text-zinc-500">
                {t.stats.sessionsCaption(sessions)}
                {sessions < stats.totals.views && <> · {t.stats.pendingHint}</>}
              </p>
            </Section>
            <Section title={t.stats.engagement}>
              <div className="space-y-1.5">
                <StatBar
                  label={t.stats.mobile}
                  value={stats.totals.mobile}
                  total={stats.totals.views}
                />
                <StatBar
                  label={t.stats.desktop}
                  value={stats.totals.desktop}
                  total={stats.totals.views}
                />
                <StatBar label={t.stats.interactionRate} value={eng.interacted} total={sessions} />
                <StatBar label={t.stats.fullscreen} value={eng.fullscreen} total={sessions} />
                <StatBar label={t.stats.bounceRate} value={eng.bounces} total={sessions} />
              </div>
            </Section>
          </div>

          <Section title={t.stats.scenes}>
            {stats.scenes.length === 0 ? (
              <p className="py-4 text-center text-sm text-zinc-400">{t.stats.scenesEmpty}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-zinc-400">
                    <tr>
                      <th className="pb-2 font-medium">{t.stats.sceneCol}</th>
                      <th className="pb-2 text-right font-medium">{t.stats.sceneViews}</th>
                      <th className="pb-2 pl-4 font-medium">{t.stats.sceneReach}</th>
                      <th className="pb-2 text-right font-medium">{t.stats.sceneAvg}</th>
                      <th className="pb-2 text-right font-medium">{t.stats.sceneEntries}</th>
                      <th className="pb-2 text-right font-medium">{t.stats.sceneExits}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.scenes.map((s) => (
                      <SceneRow
                        key={s.id}
                        scene={s}
                        sessions={sessions}
                        fmtDuration={fmtDuration}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          <div className="grid gap-4 sm:grid-cols-2">
            <Section title={t.stats.sources}>
              <div className="space-y-1.5">
                {stats.referrers.map((r) => (
                  <StatBar
                    key={r.domain}
                    label={r.domain}
                    value={r.views}
                    total={stats.totals.views}
                  />
                ))}
                {directViews > 0 && (
                  <StatBar label={t.stats.direct} value={directViews} total={stats.totals.views} />
                )}
              </div>
            </Section>
            <Section title={t.stats.languages}>
              <div className="space-y-1.5">
                {stats.languages.map((l) => (
                  <StatBar
                    key={l.lang}
                    label={langName(l.lang)}
                    value={l.views}
                    total={stats.totals.views}
                  />
                ))}
              </div>
            </Section>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Tagesverlauf: zwei Balken je Punkt (Aufrufe, Besucher), Datums-Marken an der Achse,
 * Hover-Tooltip mit Datum und Werten. Kein Chart-Library — ein SVG, das die Breite füllt.
 * Über lange Zeiträume fasst groupDaily mehrere Tage zu einem Balken zusammen.
 */
function TrendChart({
  points,
  fmtDay,
  fmtDate,
}: {
  points: TrendPoint[];
  fmtDay: (date: string) => string;
  fmtDate: (date: string) => string;
}) {
  const t = useT();
  const [hover, setHover] = useState<number | null>(null);
  const count = points.length;
  const max = Math.max(1, ...points.map((d) => d.views));
  const onMove = (e: PointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const i = Math.floor(((e.clientX - rect.left) / rect.width) * count);
    setHover(i >= 0 && i < count ? i : null);
  };
  const active = hover !== null ? points[hover] : undefined;
  // Marken vom letzten Balken aus rückwärts — so trägt der aktuelle Tag immer ein Datum
  const step = tickStep(count, points[0]?.span ?? 1);
  const ticks = points.map((d, i) => i).filter((i) => (count - 1 - i) % step === 0);
  const center = (i: number) => `${((i + 0.5) / count) * 100}%`;

  return (
    <div>
      <div
        className="relative touch-none pt-16"
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
        data-chart
      >
        {active && hover !== null && (
          <div
            className="pointer-events-none absolute top-16 z-10 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-xs shadow-md dark:border-zinc-700 dark:bg-zinc-800"
            style={{ left: center(hover) }}
          >
            <p className="font-medium">
              {active.span === 1
                ? fmtDate(active.date)
                : `${fmtDay(active.date)} – ${fmtDate(active.endDate)}`}
            </p>
            <p className="mt-0.5 flex items-center gap-1.5 tabular-nums">
              <span className="size-2 rounded-sm bg-blue-600 dark:bg-blue-500" />
              {active.views} {t.stats.views}
            </p>
            <p className="flex items-center gap-1.5 tabular-nums">
              <span className="size-2 rounded-sm bg-amber-600" />
              {active.visitors} {t.stats.visitors}
            </p>
          </div>
        )}
        <svg
          viewBox={`0 0 ${count * 8} 100`}
          preserveAspectRatio="none"
          className="h-36 w-full"
          role="img"
          aria-label={t.stats.viewsPerDay}
        >
          {hover !== null && (
            <rect
              x={hover * 8}
              y={0}
              width={8}
              height={100}
              className="fill-zinc-100 dark:fill-zinc-800"
            />
          )}
          {points.map((d, i) => {
            const h = d.views > 0 ? Math.max(3, (d.views / max) * 96) : 1.5;
            const hv = d.visitors > 0 ? Math.max(3, (d.visitors / max) * 96) : 1.5;
            return (
              <g key={d.date}>
                <rect
                  x={i * 8 + 0.8}
                  y={100 - h}
                  width={3.1}
                  height={h}
                  rx={0.8}
                  className={d.views > 0 ? VIEWS_FILL : "fill-zinc-200 dark:fill-zinc-700"}
                />
                <rect
                  x={i * 8 + 4.3}
                  y={100 - hv}
                  width={3.1}
                  height={hv}
                  rx={0.8}
                  className={d.visitors > 0 ? VISITORS_FILL : "fill-zinc-200 dark:fill-zinc-700"}
                />
              </g>
            );
          })}
        </svg>
      </div>
      <div className="relative mt-1 h-4 text-[11px] text-zinc-400 dark:text-zinc-500">
        {ticks.map((i) => (
          <span
            key={i}
            className="absolute -translate-x-1/2 whitespace-nowrap"
            style={{ left: center(i) }}
          >
            {fmtDay(points[i]!.date)}
          </span>
        ))}
      </div>
      <div className="mt-3 flex gap-4 text-xs text-zinc-500 dark:text-zinc-400">
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm bg-blue-600 dark:bg-blue-500" />
          {t.stats.views}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm bg-amber-600" />
          {t.stats.visitors}
        </span>
      </div>
    </div>
  );
}

function SceneRow({
  scene,
  sessions,
  fmtDuration,
}: {
  scene: TourStatsScene;
  sessions: number;
  fmtDuration: (s: number) => string;
}) {
  const reach = pct(scene.sessions, sessions);
  const avg = scene.sessions > 0 ? Math.round(scene.seconds / scene.sessions) : 0;
  return (
    <tr className="border-t border-zinc-100 dark:border-zinc-800">
      <td className="py-2 pr-3">
        <div className="flex items-center gap-3">
          <div className="h-9 w-16 shrink-0 overflow-hidden rounded-md bg-zinc-100 dark:bg-zinc-800">
            {scene.previewUrl && (
              <img src={scene.previewUrl} alt="" className="size-full object-cover" />
            )}
          </div>
          <span className="truncate">{scene.name}</span>
        </div>
      </td>
      <td className="py-2 text-right tabular-nums">{scene.views}</td>
      <td className="py-2 pl-4">
        <div className="flex items-center gap-2">
          <span className="h-1.5 w-24 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
            <span className="block h-full rounded-full bg-accent" style={{ width: `${reach}%` }} />
          </span>
          <span className="tabular-nums text-zinc-500 dark:text-zinc-400">{reach} %</span>
        </div>
      </td>
      <td className="py-2 text-right tabular-nums">
        {scene.sessions > 0 ? fmtDuration(avg) : "–"}
      </td>
      <td className="py-2 text-right tabular-nums">{scene.entries}</td>
      <td className="py-2 text-right tabular-nums">{scene.exits}</td>
    </tr>
  );
}
